/**
 * This file is the entry point for the React app, it sets up the root
 * element and renders the App component to the DOM.
 *
 * It is included in `src/index.html`.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./orpc-client";
import { router } from "./router";
import { initPosthog, posthog, sanitizeUrl } from "./lib/posthog";
import type { PendingPageview } from "./types/posthog";
import { initErrorReporter } from "./lib/error-reporter";
import { ErrorBoundary } from "./components/error-boundary";
import { captureUtmFromUrl } from "./utils/utm";
import {
  applyActiveWorkspaceParam,
  hasActiveWorkspaceParam,
} from "./utils/active-workspace";

// Install window error handlers before any React render so early crashes report.
initErrorReporter();

// Capture UTM params on initial load. Signup happens near the top of the funnel.
captureUtmFromUrl();

// Queue pageviews fired before initPosthog() resolves, then flush.
const pageviewQueue: PendingPageview[] = [];
let posthogReady = false;

initPosthog().then(() => {
  posthogReady = true;
  for (const pv of pageviewQueue) {
    posthog.capture("$pageview", { $current_url: pv.url, path: pv.path });
  }
  pageviewQueue.length = 0;
});

router.subscribe("onResolved", ({ toLocation }) => {
  const pv: PendingPageview = {
    url: sanitizeUrl(window.location.href),
    path: toLocation.pathname,
  };
  if (posthogReady) {
    posthog.capture("$pageview", { $current_url: pv.url, path: pv.path });
  } else {
    pageviewQueue.push(pv);
  }
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}

const elem = document.getElementById("root")!;
const app = (
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>
);

/** Mounts the app, reusing the HMR-persisted root in development. */
function render() {
  if (import.meta.hot) {
    // With hot module reloading, `import.meta.hot.data` is persisted.
    const root = (import.meta.hot.data.root ??= createRoot(elem));
    root.render(app);
  } else {
    // The hot module reloading API is not available in production.
    createRoot(elem).render(app);
  }
}

// A `?orgId=` deep link switches the active workspace before the first paint, so
// the opening render already queries the right one.
if (hasActiveWorkspaceParam()) {
  void applyActiveWorkspaceParam().finally(render);
} else {
  render();
}
